import React, { useState } from "react";
import { normalizeRecoveryCode } from "../lib/tokiPonaData";
import { Icon, Dots } from "./tokiPonaUi";

/* ============================================================
   o kama sona — "o tawa e sona" / cross-device carry.
   Progress is saved per-device under an anonymous code (the
   Firestore user id). This screen surfaces that code to copy, and
   accepts a code pasted from another device to re-bind this device
   to that learner. Degrades gracefully when the cloud isn't set up.

   Props:
     code     : string   this device's code (user id)
     isCloud  : boolean  whether Firestore sync is configured
     onBack   : () => void
     onCarry  : (code) => void   re-bind this device to a pasted code
   ============================================================ */
const TokiPonaSync = ({ code, isCloud, onBack, onCarry }) => {
  const [entry, setEntry] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError("couldn't copy automatically — select the code and copy it.");
    }
  };

  const carry = () => {
    const clean = normalizeRecoveryCode(entry);
    if (!clean) {
      setError("that doesn't look like a valid code.");
      return;
    }
    if (clean === code) {
      setError("that's already this device's code.");
      return;
    }
    onCarry(clean);
  };

  return (
    <div className="tp-root">
      <div className="tp-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onBack} aria-label="back">
            <Icon name="back" />
          </button>
          <div className="subbar-title">o tawa e sona · carry your progress</div>
        </div>
        <div className="scroll">
          {isCloud ? (
            <>
              <p className="games-intro">
                Your xp, streak and unlocked lessons save to the cloud under the
                code below. Copy it somewhere safe, then paste it on another
                device to pick up exactly where you left off.
              </p>

              <div className="tp-sync-block">
                <div className="tp-sync-h">this device's code</div>
                <div className="tp-code">
                  <code>{code}</code>
                  <button className="btn" onClick={copy}>
                    {copied ? "copied!" : "copy"}
                  </button>
                </div>
              </div>

              <div className="tp-sync-block">
                <div className="tp-sync-h">carry it here from another device</div>
                <p className="tp-sync-note">
                  Paste a code to load that learner here. Your current code stays
                  valid — keep it if you want to switch back.
                </p>
                <div className="tp-code-entry">
                  <input
                    value={entry}
                    onChange={(e) => {
                      setEntry(e.target.value);
                      setError("");
                    }}
                    placeholder="paste a code"
                    aria-label="progress code"
                  />
                  <button
                    className="btn loje"
                    disabled={!entry.trim()}
                    onClick={carry}
                  >
                    carry
                  </button>
                </div>
                {error && <div className="tp-sync-err">{error}</div>}
              </div>
            </>
          ) : (
            <p className="games-intro">
              cloud sync isn't set up on this site, so your progress is saved
              only in this browser. There's no portable code yet — once cloud
              sync is configured, you'll be able to carry your progress between
              devices from here.
            </p>
          )}
          <div className="footnote">
            <Dots />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokiPonaSync;
